import config from '../config'

export type UserAdmin = {
  id: number
  username: string
  email: string
  full_name: string
  is_active: boolean
  is_superuser?: boolean
  permissions: string[]
}

export type UserCreateAdmin = {
  username: string
  email?: string
  full_name: string
  password: string
  is_active?: boolean
}

export type UserUpdateAdmin = {
  username?: string
  email?: string
  full_name?: string
  password?: string
  is_active?: boolean
}

export type UserPermissionsUpdate = {
  permission_names: string[]
}

export type RawProjectPermissionItem = {
  project_id: number
  actions: { [key: string]: boolean }
}

export type RawProjectPermissionResponseItem = {
  project_id: number
  project_name: string
  upload_mode: string
  actions: { [key: string]: boolean }
}

export type LabelProjectPermissionItem = {
  project_id: number
  actions: { [key: string]: boolean }
}

export type LabelProjectPermissionResponseItem = {
  project_id: number
  project_name: string
  actions: { [key: string]: boolean }
}

export type DatasetProjectPermissionItem = {
  project_id: number
  actions: { [key: string]: boolean }
}

export type DatasetProjectPermissionResponseItem = {
  project_id: number
  project_name: string
  actions: { [key: string]: boolean }
}

export type TestDatasetProjectPermissionItem = {
  project_id: number
  actions: { [key: string]: boolean }
}

export type TestDatasetProjectPermissionResponseItem = {
  project_id: number
  project_name: string
  actions: { [key: string]: boolean }
}

class UserService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async listUsers(params?: { q?: string; skip?: number; limit?: number }): Promise<UserAdmin[]> {
    const sp = new URLSearchParams()
    if (params?.q) sp.set('q', params.q)
    if (params?.skip != null) sp.set('skip', String(params.skip))
    if (params?.limit != null) sp.set('limit', String(params.limit))
    const resp = await fetch(`${this.baseUrl}/users/?${sp.toString()}`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async listAvailablePermissions(): Promise<string[]> {
    const resp = await fetch(`${this.baseUrl}/auth/permissions`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async setUserPermissions(userId: number, permissionNames: string[]): Promise<UserAdmin> {
    const payload: UserPermissionsUpdate = { permission_names: permissionNames }
    const resp = await fetch(`${this.baseUrl}/users/${userId}/permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async getRawProjectPermissions(userId: number): Promise<RawProjectPermissionResponseItem[]> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/raw-project-permissions`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async setRawProjectPermissions(userId: number, items: RawProjectPermissionItem[]): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/raw-project-permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(items),
    })
    if (!resp.ok) throw new Error(await resp.text())
  }

  async createUser(payload: UserCreateAdmin): Promise<UserAdmin> {
    const resp = await fetch(`${this.baseUrl}/users/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async getLabelProjectPermissions(userId: number): Promise<LabelProjectPermissionResponseItem[]> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/label-project-permissions`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async setLabelProjectPermissions(userId: number, items: LabelProjectPermissionItem[]): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/label-project-permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(items),
    })
    if (!resp.ok) throw new Error(await resp.text())
  }

  async updateUser(userId: number, payload: UserUpdateAdmin): Promise<UserAdmin> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async getDatasetProjectPermissions(userId: number): Promise<DatasetProjectPermissionResponseItem[]> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/dataset-project-permissions`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async setDatasetProjectPermissions(userId: number, items: DatasetProjectPermissionItem[]): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/dataset-project-permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(items),
    })
    if (!resp.ok) throw new Error(await resp.text())
  }

  async getTestDatasetProjectPermissions(userId: number): Promise<TestDatasetProjectPermissionResponseItem[]> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/test-dataset-project-permissions`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async setTestDatasetProjectPermissions(userId: number, items: TestDatasetProjectPermissionItem[]): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}/test-dataset-project-permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(items),
    })
    if (!resp.ok) throw new Error(await resp.text())
  }

  async deleteUser(userId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/users/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) throw new Error(await resp.text())
  }
}

export default new UserService()


